import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useUploadSingleFile } from '@/shared/lib/upload/useUploadSingleFile';
import { useDeleteFile } from '@/shared/lib/upload/useDeleteFile';
import {
  useUpdateProfileMutation,
  type ProfileData,
} from '@/features/profile/api/useProfileQueries';
import { getErrorMessage } from '@/shared/api/errors';

const MAX_PROFILE_PIC_BYTES = 5 * 1024 * 1024;

function profilePicUrl(profilePic: ProfileData['profilePic']): string | null {
  if (!profilePic) return null;
  if (typeof profilePic === 'string') return profilePic;
  return profilePic.url || null;
}

function profilePicMediaId(
  profilePic: ProfileData['profilePic'],
): string | null {
  if (!profilePic || typeof profilePic === 'string') return null;
  const id =
    profilePic.id ||
    (profilePic as { _id?: string })._id ||
    (profilePic as { mediaId?: string }).mediaId;
  return id ? String(id) : null;
}

/** Upload a device image and patch the user profilePic field. */
export function useProfilePhotoUpload(user?: ProfileData) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const upload = useUploadSingleFile();
  const deleteFile = useDeleteFile();
  const updateProfile = useUpdateProfileMutation();

  useEffect(() => {
    setPreview(profilePicUrl(user?.profilePic));
  }, [user?.profilePic]);

  const busy = upload.isPending || updateProfile.isPending;

  const openPicker = () => {
    if (busy) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || busy) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }
    if (file.size > MAX_PROFILE_PIC_BYTES) {
      toast.error('Profile photo must be under 5MB');
      return;
    }

    const previousId = profilePicMediaId(user?.profilePic);
    const previousPreview = profilePicUrl(user?.profilePic);

    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    try {
      const result = await upload.mutateAsync({
        file,
        moduleName: 'profile',
      });
      const mediaId = result?.data?.mediaId as string | undefined;
      if (!mediaId) throw new Error('Upload did not return a media id');

      try {
        await updateProfile.mutateAsync({ profilePic: mediaId });
      } catch {
        // Mutation already toasts.
        setPreview(previousPreview);
        return;
      }

      if (previousId && previousId !== String(mediaId)) {
        void deleteFile.mutateAsync({ mediaId: previousId }).catch(() => {});
      }
    } catch (err) {
      setPreview(previousPreview);
      toast.error(getErrorMessage(err, 'Failed to update profile photo'));
    }
  };

  return {
    fileInputRef,
    preview,
    busy,
    openPicker,
    handleFileChange,
  };
}
